import { MapPin } from 'lucide-react'
import { useROIStore } from '../store'
import { INDUSTRIES, PROVIDERS, PRODUCT_TYPES, formatNumber, formatCurrency } from '../data'
import type { Region } from '../types'

interface CustomerRegionSummaryProps {
  region: Region
}

export function CustomerRegionSummary({ region }: CustomerRegionSummaryProps) {
  const { getRegionCalculation } = useROIStore()
  const calculation = getRegionCalculation(region.id)

  const industryName = INDUSTRIES.find(i => i.id === region.industry)?.name || 'Not specified'
  const providerName = PROVIDERS.find(p => p.id === region.provider)?.name || 'Not specified'
  const productName = PRODUCT_TYPES.find(p => p.id === region.product)?.name || 'Not specified'
  const improvement = calculation?.improvement ?? 0
  const newRate = calculation?.newRate ?? region.passRate

  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg overflow-hidden">
      {/* Region Header */}
      <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)]">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
            <MapPin size={16} className="text-primary" />
          </div>
          <div>
            <span className="font-medium text-[var(--text-primary)]">
              {region.name || region.country || 'Region'}
            </span>
            {region.name && region.country && (
              <span className="text-xs text-[var(--text-muted)] ml-2">{region.country}</span>
            )}
          </div>

          {improvement > 0 && (
            <span className="bg-primary/20 text-primary px-2 py-0.5 rounded text-xs font-medium">
              +{improvement.toFixed(1)}% improvement
            </span>
          )}
        </div>

        {/* Pass rate summary */}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-[var(--text-secondary)]">{region.passRate.toFixed(1)}%</span>
          <span className="text-gray-600">→</span>
          <span className="font-medium text-success">{newRate.toFixed(1)}%</span>
        </div>
      </div>

      {/* Detail rows */}
      <div className="p-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <DetailItem label="Industry" value={industryName} />
          <DetailItem label="Current Provider" value={providerName} />
          <DetailItem label="Current Product" value={productName} />
          <DetailItem label="Current Pass Rate" value={`${region.passRate}%`} />
          <DetailItem label="Monthly Volume" value={formatNumber(region.volume)} />
          <DetailItem label="Value per Customer" value={formatCurrency(region.valuePerCustomer)} />
          {calculation && (
            <>
              <DetailItem
                label="Additional Conversions"
                value={`+${formatNumber(Math.round(calculation.additionalConversions))}/mo`}
                highlight
              />
              <DetailItem
                label="Annual Value Added"
                value={formatCurrency(calculation.annualValue)}
                highlight
              />
            </>
          )}
        </div>

        {/* FrankieOne Stack */}
        {region.stack.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
            <h5 className="text-xs font-medium text-primary uppercase tracking-wide mb-3">
              Verification Pipeline
            </h5>
            <div className="flex flex-wrap gap-2">
              {region.stack.map((item, i) => (
                <div
                  key={item.profile}
                  className="flex items-center gap-2 bg-[#1e2a3a] border border-[#2a3a4a] rounded-lg px-3 py-2"
                >
                  <span className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-medium">
                    {i + 1}
                  </span>
                  <span className="text-sm text-[var(--text-primary)]">{item.description}</span>
                  <span className="text-xs text-[var(--text-muted)]">+{item.incrementalLift}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function DetailItem({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide mb-0.5">{label}</div>
      <div className={`text-sm font-medium ${highlight ? 'text-success' : 'text-[var(--text-primary)]'}`}>
        {value}
      </div>
    </div>
  )
}
